import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import { t } from 'i18next';
import _ from 'lodash';
import moment from 'moment-timezone';
import { setHtmlCKEditor } from '@/annotation/ckEditor.utilities';
import {
  canModifyDocument,
  clearPropertyOverridesForContent,
  computeCapsuleOffset,
  forceRefreshAllContent,
  forceRefreshContentUsingAssetSelection,
  forceRefreshContentUsingDate,
  formatAssetSelectionFromApiOutput,
  formatAssetSelectionToApiInput,
  formatContentFromApiOutput,
  formatContentToApiInput,
  formatDateRangeFromApiOutput,
  formatDateRangeToApiInput,
  getStrippedAndValidatedDocument,
  maybeClearVisualizationSpecificState,
  refreshContentUsingDate,
  replaceContentIfExists,
  subscribeToReport,
} from '@/annotation/reportContent.utilities';
import { DEBOUNCE } from '@/core/core.constants';
import { sqPdfExportStore, sqReportStore, sqWorkbenchStore, sqWorkbookStore } from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { PUSH_IGNORE } from '@/core/flux.service';
import utilities from '@/core/utilities';
import { API_TYPES, NG_IF_WAIT, NUMBER_CONVERSIONS } from '@/main/app.constants';
import { getWorkbooksLink, goTo } from '@/main/routing.utilities';
import { sandboxUrlParams } from '@/reportConfig/components/sandboxUrlParams.util';

import { tryParsePageMargin } from '@/reportEditor/pdfMargin.utilities';
import { ReportEditingStateEvent, ReportUpdateMessageType } from '@/reportEditor/report.constants';
import { headlessRenderMode } from '@/services/headlessCapture.utilities';
import { isAdvancedDateRangeSwapEnabled } from '@/services/systemConfiguration.utilities';
import { doTrack } from '@/track/track.service';
import { nameAndDescriptionFromDocument } from '@/utilities/annotation.utilities';
import { sqTimezones } from '@/utilities/datetime.constants';
import { computeCapsules, computeScalar, getDependencies } from '@/utilities/formula.utilities';
import { isCanceled } from '@/utilities/http.utilities';
import { toggleEditor } from '@/utilities/migration.utilities';
import {
  getCursorPosition,
  getHtmlReportEditor,
  getScrollOffset,
  setCursorPosition,
  setScrollOffset,
} from '@/utilities/reportEditor.utilities';
import { emit } from '@/utilities/socket.utilities';
import { errorToast, infoToast, warnToast } from '@/utilities/toast.utilities';
import { debounceAsync } from '@/utilities/utilities';
import { isPresentationWorkbookMode, isViewOnlyWorkbookMode } from '@/utilities/workbookMode.utilities';
import { setTimezone } from '@/worksheet/worksheet.actions';
import { sqAnnotationsApi } from '@seeqdev/api/endpoint/AnnotationsApi';
import { sqContentApi } from '@seeqdev/api/endpoint/ContentApi';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { sqWorkbooksApi } from '@seeqdev/api/endpoint/WorkbooksApi';
import type { BulkEditMode } from '@/reportEditor/components/reportContentModal/bulkEdit/reportContent.constants';
import type {
  AssetSelection,
  Content,
  ContentDisplayMetadata,
  DateRange,
  DateRangeCondition,
  DateRangeSwapInfo,
  DecoratedContent,
  InteractiveReportContent,
  Range,
  ReportContentSummary,
  ReportSchedule,
  ReportUpdateMessage,
  ReportUpdateMessageWithSpecificUpdates,
  SandboxMode,
} from '@/reportEditor/report.constants';
import type { ReportContentScale, ReportContentShape, ReportContentSize } from '@/reportEditor/report.constants';
import type { ReportStore } from '@/reportEditor/report.store';
import type { AnnotationOutputV1 } from '@seeqdev/api/model/AnnotationOutputV1';
import type { AssetSelectionInputV1 } from '@seeqdev/api/model/AssetSelectionInputV1';
import type { ContentOutputV1 } from '@seeqdev/api/model/ContentOutputV1';
import type { ContentWithMetadataListOutputV1 } from '@seeqdev/api/model/ContentWithMetadataListOutputV1';
import type { DateRangeInputV1 } from '@seeqdev/api/model/DateRangeInputV1';

const DATA_SEEQ_CONTENT_PENDING_REGEX = new RegExp(
  `${SeeqNames.TopicDocumentAttributes.DataSeeqContentPending}="(.+?)"`,
  'g',
);

export const debouncedImageStateChanged = _.debounce(imageStateChanged, DEBOUNCE.MEDIUM);
export const debouncedOnReport = debounceAsync(onReport);

/**
 * Finds an existing report and sets it in the store.
 *
 * @param reportId - a report ID
 * @param transformData - Optional function that transforms the fetched data. If it returns ture the report and
 * content is re-fetched.
 * @param isLoadingSandbox - Only used when loading sandbox to prevent infinite loop
 * @param isSandboxDisabled - Only used if attempting to load sandbox resulted in a permission error and it is
 * rolling back to the original report.
 * @param reportStore - The report store to use, defaults to sqReportStore. Ideally passed in from a reactive component.
 * @returns Resolves when the report, its content, and its date ranges are loaded
 */
export async function loadReport(
  reportId: string,
  transformData = (data: ContentWithMetadataListOutputV1) => Promise.resolve(false),
  isLoadingSandbox = false,
  isSandboxDisabled = false,
  reportStore = sqReportStore,
): Promise<any> {
  if (reportStore.sandboxMode.enabled && reportStore.id === reportId) {
    // We do not want to re-load when we are in sandbox mode.
    return;
  }
  const fetchReportAndContent = () =>
    Promise.all([
      sqAnnotationsApi.getAnnotation({ id: reportId }),
      sqContentApi.getContentsWithAllMetadata({ reportId }),
    ]);

  try {
    let [reportResponse, contentResponse] = await fetchReportAndContent();
    const shouldReload = await transformData(contentResponse.data);
    // Reload everything once saved
    if (shouldReload) {
      [reportResponse, contentResponse] = await fetchReportAndContent();
    }
    const report = reportResponse.data;
    const contentMetadata = contentResponse.data;
    if (!report.ckEnabled) {
      doTrack('CKEditor', 'Upgrade');
      await toggleEditor(reportId);
      return loadReport(reportId, transformData, isLoadingSandbox);
    }
    // Sandbox mode can be disabled via a query parameter. Useful for use-cases where they want to avoid the cloning
    // that happens with sandbox mode, but don't need to be able to edit date ranges or asset selections.
    const skipSandboxMode =
      new URLSearchParams(window.location.search).get('skipSandboxMode')?.toLowerCase() === 'true';
    const effectiveIsSandboxDisabled = isSandboxDisabled || isPresentationWorkbookMode() || skipSandboxMode;
    manageLoadedReportContent(report, contentMetadata, effectiveIsSandboxDisabled);
    doTrack('Topic', 'Document Loaded', report.fixedWidth ? 'With Fixed Width' : 'With Auto Width');
    if (
      !skipSandboxMode &&
      !isLoadingSandbox &&
      !isSandboxDisabled &&
      !headlessRenderMode() &&
      isViewOnlyWorkbookMode() &&
      !isPresentationWorkbookMode()
    ) {
      return exposedForTesting.createSandboxAndLoadTempReport(reportStore);
    }
  } catch (e) {
    if (!isCanceled(e)) {
      errorToast({ httpResponseOrError: e });
      goTo(getWorkbooksLink());
    }
  }
}

export function manageLoadedReportContent(
  report: AnnotationOutputV1,
  data: ContentWithMetadataListOutputV1,
  isSandboxDisabled: boolean,
): void {
  setTimezone(report.timezone === '' ? undefined : _.find(sqTimezones.timezones, { name: report.timezone }));

  flux.dispatch('REPORT_RESET');
  flux.dispatch('REPORT_REMOVE_ALL_CONTENT', undefined, PUSH_IGNORE);
  flux.dispatch('REPORT_REMOVE_ALL_DATE_RANGES', undefined, PUSH_IGNORE);

  // The content must be set before the report to ensure hash code is present for the image urls
  // Make sure we're getting date ranges from the report
  const contentItems = _.map(data.contentItems, (contentWithMetadata) =>
    formatContentFromApiOutput(contentWithMetadata),
  );

  flux.dispatch('REPORT_SET_ALL_CONTENT', contentItems, PUSH_IGNORE);

  const dateRanges = _.map(data.dateRanges, (dateRange) => formatDateRangeFromApiOutput(dateRange));
  flux.dispatch('REPORT_SET_ALL_DATE_RANGES', dateRanges, PUSH_IGNORE);
  const assetSelections: AssetSelection[] = _.map(data.assetSelections, (assetSelection) =>
    formatAssetSelectionFromApiOutput(assetSelection),
  );
  flux.dispatch('REPORT_SET_ALL_ASSET_SELECTIONS', assetSelections, PUSH_IGNORE);
  flux.dispatch('REPORT_SET', report, PUSH_IGNORE);
  flux.dispatch('REPORT_SET_IS_SANDBOX_DISABLED', { isDisabled: isSandboxDisabled }, PUSH_IGNORE);

  const firstInvalidMarginValue = _.find(
    [report.marginLeft, report.marginRight, report.marginTop, report.marginBottom],
    (marginValue): marginValue is string => !_.isEmpty(marginValue) && _.isUndefined(tryParsePageMargin(marginValue)),
  );

  if (firstInvalidMarginValue) {
    errorToast({ messageKey: `'${firstInvalidMarginValue}' is not a valid margin value` });
  }

  // Update PDF export settings from report properties
  // Note: This function loads report data and updates the singleton store for backward compatibility.
  // Components reading from provider-backed hooks should use their own store instance.
  if (
    report.pageSize ||
    report.pageOrientation ||
    report.marginLeft ||
    report.marginRight ||
    report.marginTop ||
    report.marginBottom
  ) {
    sqPdfExportStore.actions.setAllOptions({
      pageSize: report.pageSize || '',
      pageOrientation: report.pageOrientation || '',
      marginLeft: report.marginLeft || '',
      marginRight: report.marginRight || '',
      marginTop: report.marginTop || '',
      marginBottom: report.marginBottom || '',
    });
  }
}

let loadingSandboxModePromise: Promise<void> | undefined;
// A map of update request group keys to promises for documents that are being updated.
const currentUpdateRequests: Record<string, Promise<unknown>> = {};
// A map of update request group keys to {id, document } to update. Used to defer update requests when one is
// already in process.
const deferredUpdateRequests: Record<string, { id: string; document: string }> = {};

/**
 * Updates the report on the backend and sets the report in the report store.
 *
 * @param id - report ID
 * @param document - the HTML document
 * @param  stepToNow - if true, steps scheduled report up to now
 * @returns {Promise} a promise that resolves when the report has been updated
 */
export function updateReport(id: string, document = getHtmlReportEditor(), stepToNow = false): Promise<any> {
  const isSandboxActive = sqReportStore.sandboxMode.enabled;

  if (isSandboxActive) {
    document = sqReportStore.document!;
  }
  const workbookId = isSandboxActive
    ? sqReportStore.sandboxMode.sandboxedWorkbookId
    : sqWorkbenchStore.stateParams.workbookId;
  const worksheetId = isSandboxActive
    ? sqReportStore.sandboxMode.sandboxedWorksheetId
    : sqWorkbenchStore.stateParams.worksheetId;

  exposedForTesting.onEditingStateEvent(ReportEditingStateEvent.SaveStarted);
  const updateRequestGroup = `reportSave-${id}`;
  if (_.isUndefined(document)) {
    return Promise.reject('Document is undefined');
  }
  if (updateRequestGroup in currentUpdateRequests) {
    // this will overwrite a previously deferred request for this group, but that's OK
    deferredUpdateRequests[updateRequestGroup] = { id, document };
    return currentUpdateRequests[updateRequestGroup]!;
  } else {
    const { description, name } = nameAndDescriptionFromDocument(document);

    // Update the document to include img[src] if not available to account for the front end transitioning the
    // loadingSpinners
    let strippedAndValidatedDocument = getStrippedAndValidatedDocument(document);

    currentUpdateRequests[updateRequestGroup] = sqAnnotationsApi
      .updateAnnotation(
        {
          name,
          description,
          type: API_TYPES.REPORT,
          document: strippedAndValidatedDocument,
          reportInput: {
            cronSchedule: sqReportStore.reportSchedule?.cronSchedule,
            background: !!sqReportStore.reportSchedule?.background,
            enabled: !!sqReportStore.reportSchedule?.enabled,
            stepToNow,
          },
        },
        { id },
      )
      .then(({ data: report }) => {
        onEditingStateEvent(ReportEditingStateEvent.SaveComplete);
        if (id === sqReportStore.id) {
          return sqContentApi.getContentsWithAllMetadata({ reportId: report.id }).then(({ data }) => {
            const contentItems = _.map(data.contentItems, (contentWithMetadata) =>
              formatContentFromApiOutput(contentWithMetadata),
            );
            flux.dispatch('REPORT_SET_ALL_CONTENT', contentItems);
            const dateRanges = _.map(data.dateRanges, (dateRange) => formatDateRangeFromApiOutput(dateRange));
            flux.dispatch('REPORT_SET_ALL_DATE_RANGES', dateRanges);
            const assetSelections = _.map(data.assetSelections, (assetSelection) =>
              formatAssetSelectionFromApiOutput(assetSelection),
            );
            flux.dispatch('REPORT_SET_ALL_ASSET_SELECTIONS', assetSelections);
            flux.dispatch('REPORT_SET', { ...report, document: strippedAndValidatedDocument }, PUSH_IGNORE);
            return report;
          });
        }
        return report;
      })
      .catch((error) => {
        onEditingStateEvent(ReportEditingStateEvent.Offline);
        errorToast({ httpResponseOrError: error });
      })
      .finally(() => {
        delete currentUpdateRequests[updateRequestGroup];
        if (deferredUpdateRequests[updateRequestGroup]) {
          const deferredUpdateData = deferredUpdateRequests[updateRequestGroup];
          delete deferredUpdateRequests[updateRequestGroup];
          return updateReport(deferredUpdateData.id, deferredUpdateData.document);
        }
      });
    return currentUpdateRequests[updateRequestGroup];
  }
}

export async function exitSandboxMode(store: ReportStore = sqReportStore): Promise<void> {
  if (!store.sandboxMode.enabled) {
    return;
  }

  // Deactivate schedule if it exists, to prevent wasted jobs running
  await exposedForTesting.saveReportSchedule({
    enabled: false,
    background: false,
    cronSchedule: store.reportSchedule?.cronSchedule,
  });
  const reportId = store.sandboxMode.originalReportId!;
  exposedForTesting.setSandboxMode({
    enabled: false,
    isModified: false,
    sandboxedWorksheetId: null,
    sandboxedWorkbookId: null,
    originalReportId: null,
    originalWorksheetId: null,
    useRendererUser: false,
  });
  await extraExposedForTesting.loadReport(reportId);
  doTrack('Sandbox Mode', 'Exit Sandbox');
}

/**
 * Parses the HTML and returns a list of Seeq content ids
 *
 * @param {string} document - HTML document contents to parse
 * @returns {string[]} An array of seeq content
 */
export function parseSeeqContentIdsFromHtml(document: string): string[] {
  if (!document) return [];
  return _.map([...document.matchAll(DATA_SEEQ_CONTENT_PENDING_REGEX)], ([, id]) => id);
}

export function imageStateChanged() {
  flux.dispatch('REPORT_IMAGE_STATE_CHANGED', null, PUSH_IGNORE);
}

export function iframeRefreshCountChanged() {
  flux.dispatch('REPORT_IFRAME_REFRESH_COUNT_CHANGED', null, PUSH_IGNORE);
}

/**
 * Sets the editing state in the report store
 *
 * @param {string} event - the editing state event that occurred
 */
export function onEditingStateEvent(event: ReportEditingStateEvent) {
  flux.dispatch('REPORT_EDITING_STATE_EVENT', { event }, PUSH_IGNORE);
}

/**
 * Called when the user's connection is created, broken, or restored
 *
 * @param {boolean} isOffline - whether the connection is offline or not
 */
export function setIsOffline(isOffline: boolean) {
  onEditingStateEvent(isOffline ? ReportEditingStateEvent.Offline : ReportEditingStateEvent.Online);
}

/**
 * Fetches a single content along with associated date range and asset selection used in the current report,
 * optionally populating in sqReportStore.
 *
 * @param {string} id - ID of content to fetch
 * @param {boolean} [populateStore] - true to populate fetched content and dateRange in report store
 * @returns {Promise} that resolves when the content and date range have been retrieved and populated in the store.
 */
export function fetchContent(
  id: string,
  populateStore = true,
): Promise<{
  content: Content;
  dateRange: DateRange | undefined;
  assetSelection: AssetSelection | undefined;
}> {
  return sqContentApi.getContent({ id }).then(({ data }) => processContentResponse(data, populateStore));
}

/**
 * Converts a single ContentOutputV1 object into its Content, DateRange and AssetSelection counterparts, optionally
 * populating them in the store.
 *
 * @param contentOutput
 * @param populateStore
 * @returns {Object} containing .content, .dateRange and .assetSelection objects
 */
export function processContentResponse(
  contentOutput: ContentOutputV1,
  populateStore = true,
): {
  content: Content;
  dateRange: DateRange | undefined;
  assetSelection: AssetSelection | undefined;
} {
  const content = formatContentFromApiOutput(contentOutput);
  let dateRange;
  if (populateStore) {
    flux.dispatch('REPORT_SET_CONTENT', content, PUSH_IGNORE);
  }

  // If this content has a dateRange, populate it in store
  if (contentOutput.dateRange) {
    dateRange = formatDateRangeFromApiOutput(contentOutput.dateRange);
    if (populateStore) {
      flux.dispatch('REPORT_SET_DATE_RANGE', dateRange, PUSH_IGNORE);
    }
  }

  let assetSelection;
  if (contentOutput.assetSelection) {
    assetSelection = formatAssetSelectionFromApiOutput(contentOutput.assetSelection);
    if (populateStore) {
      flux.dispatch('REPORT_SET_ASSET_SELECTION', assetSelection, PUSH_IGNORE);
    }
  }

  return { content, dateRange, assetSelection };
}

/**
 * Fetches specified contents along with associated date ranges, populating in
 * sqReportStore.
 *
 * @returns {Promise} that resolves when the contents and date ranges have been retrieved and populated in the store.
 */
export function fetchMultipleContent(contentIds: string[]): Promise<Content[]> {
  return (
    _.chain(contentIds)
      .map((id: string) => fetchContent(id))
      .thru((p) => Promise.all(p))
      .value() as Promise<{ content: Content }[]>
  ).then((allContent) => allContent.map((result) => result.content)) as Promise<Content[]>;
}

/**
 * Fetches a single date range used in the current report, populating in sqReportStore.
 *
 * @param {string} id - ID of dateRange to fetch
 * @param {boolean} [populateStore] - true to populate fetched dateRange in report store
 * @returns {Promise} that resolves when the date range has been retrieved.
 */
export function fetchDateRange(
  id: string | undefined,
  populateStore = true,
): Promise<{ dateRange: DateRange; contentIds: (string | undefined)[] }> {
  return sqContentApi.getDateRange({ id: id! }).then(({ data }) => {
    const dateRange = formatDateRangeFromApiOutput(data);
    if (populateStore) {
      flux.dispatch('REPORT_SET_DATE_RANGE', dateRange, PUSH_IGNORE);
    }
    return {
      dateRange,
      contentIds: data?.content?.map?.((contentItemPreview) => contentItemPreview.id) ?? [],
    };
  });
}

/**
 * Updates the report's timezone and the worksheet store's timezone.
 * @returns A promise that resolves when API returns a response, the content store has been updated, and content
 * images have begun refreshing.
 */
export async function updateTimezone(timezone?: { name: string }): Promise<any> {
  if (!sqWorkbookStore.isTopic) {
    return Promise.resolve();
  }

  const params = {
    id: sqReportStore.id!,
    propertyName: SeeqNames.Properties.Timezone,
  };
  if (timezone) {
    await sqItemsApi.setProperty({ value: timezone.name }, params);
  } else {
    await sqItemsApi.deleteProperty(params);
  }

  setTimezone(timezone);
  const reportTimezone = timezone ? timezone.name : undefined;
  _.forEach(sqReportStore.content, ({ id: contentId, timezone: contentTimezone }) => {
    if (contentTimezone !== reportTimezone) {
      flux.dispatch('REPORT_UPDATE_CONTENT_TIMEZONE', { contentId, timezone: reportTimezone });
    }
  });
  forceRefreshAllContent();
}

/**
 * Updates or adds Seeq content to the backend and caches it in the report store
 *
 * @param {Object} content - Content to add/update.
 * @param {boolean} noEmit - when true, no ‘content changed’ message is emitted to other viewers of this report
 * @param {String} [content.id] - ID of content; missing if content is new
 * @returns {Promise} that resolves with the ID of content that was created/updated
 */
export function saveContent(content: Content, noEmit = false, isDashboard = false): Promise<Content> {
  let result: Promise<{ data: ContentOutputV1 }>;
  let wasReact: boolean;
  const contentInput = formatContentToApiInput(content);
  const worksheetId = sqWorkbenchStore.stateParams.worksheetId;
  if (!content.id) {
    wasReact = content.isReact;
    result = sqContentApi.createContent(contentInput);
  } else {
    wasReact = Boolean(sqReportStore.getContentById(content.id)?.isReact);
    // Whenever we update content, clear the cached images for that content so that we can give users a way to
    // ensure that their images are as up-to-date as possible (and also to match existing behavior).
    result = sqContentApi.updateContent(contentInput, {
      id: content.id,
      clearCache: true,
    });
  }

  return result.then(({ data }) => {
    const content = formatContentFromApiOutput(data);
    setContent(content);
    if (!isDashboard) {
      maybeClearVisualizationSpecificState(content.id!, wasReact, content.isReact);
      clearPropertyOverridesForContent(content.id!);

      if (!noEmit)
        emitReportWithSpecificUpdates(worksheetId, {
          contentIds: { updated: [content.id!] },
        });
    }
    return content;
  });
}

/**
 * Updates or adds the Seeq content to the report store
 *
 * @param content - Content to add/update
 */
export function setContent(content: Content) {
  flux.dispatch('REPORT_SET_CONTENT', content, PUSH_IGNORE);
}

/**
 * Updates or adds frontend specific metadata for the given piece of content to the report store.
 *
 * @param displayMetadata - the display metadata
 */
export function setContentDisplayMetadata(displayMetadata: ContentDisplayMetadata) {
  flux.dispatch('REPORT_SET_CONTENT_DISPLAY_METADATA', displayMetadata, PUSH_IGNORE);
}

export function setReportScheduleError(error: string, errorCode: number) {
  flux.dispatch('REPORT_SET_REPORT_SCHEDULE_ERROR', { error, errorCode }, PUSH_IGNORE);
}

/**
 * Removes the display metadata associated with the given piece of content, assuming it exists.
 */
export function removeContentDisplayMetadata(contentId: string) {
  flux.dispatch('REPORT_REMOVE_CONTENT_DISPLAY_METADATA', contentId, PUSH_IGNORE);
}

/**
 * Sets the hash code for the given piece of content.
 *
 * @param {string} contentId - ID of the content object to update
 * @param {string} hashCode - The unique identifier for the current variant of the image
 */
export function setContentHashCode(contentId: string, hashCode: string) {
  flux.dispatch('REPORT_SET_CONTENT_HASH_CODE', { contentId, hashCode }, PUSH_IGNORE);
}

export function setIsGeneratingContent(contentId: string, isGenerating: boolean) {
  flux.dispatch('REPORT_SET_IS_CONTENT_GENERATING', { contentId, isGenerating });
}

export function setContentWarning(contentId: string, warning: string) {
  flux.dispatch('REPORT_SET_CONTENT_WARNING', { contentId, warning }, PUSH_IGNORE);
}

/**
 * Retrieves the content specified by the id, removes the Archived property (if present), and populates the store.
 * If using a dateRange, ensures that the dateRange is restored as well.
 *
 * @param {string} id - ID of content to restore
 * @returns {Promise} that resolves when the item has been restored
 */
export function restoreContent(id: string): Promise<any> {
  return exposedForTesting.fetchContent(id).then(({ content, dateRange }) => {
    const promises = [];
    if (content.isArchived) {
      promises.push(exposedForTesting.saveContent({ ...content, isArchived: false }));
    }
    if (dateRange?.isArchived) {
      promises.push(exposedForTesting.saveDateRange({ ...dateRange, isArchived: false }));
    }

    return Promise.all(promises);
  });
}

/**
 * Sets the archived property on a piece of Seeq content. Note that it does NOT remove any HTML
 * elements associated with the content from the document itself; this export function assumes that the elements have
 * already been removed. Also does not remove any link to a dateRange, so that operations such as Undo can restore
 * a piece of content.
 *
 * @param {Object} content - Content to remove
 * @param {String} content.id - ID of content to remove
 * @returns {Promise} that resolves when the Content has been removed
 */
export function removeContent(content: Content, isDashboard = false) {
  return exposedForTesting.saveContent({ ...content, isArchived: true }, false, isDashboard);
}

/**
 * Update the height and width of fixed-size content after the image has been rendered. Does not save the content
 * to the backend, since the backend representation doesn't need to be updated.
 *
 * @param {string} contentId - ID of content to update
 * @param {number} width - rendered width of content, in pixels
 * @param {number} height - rendered height of content, in pixels
 */
export function setContentRenderSize(contentId: string, width: number, height: number) {
  flux.dispatch('REPORT_SET_CONTENT_RENDER_SIZE', { contentId, width, height }, PUSH_IGNORE);
}

export function updateAssetSelection(selection: AssetSelection): Promise<AssetSelection> {
  const assetSelectionInput: AssetSelectionInputV1 = formatAssetSelectionToApiInput(selection);
  return sqContentApi
    .updateAssetSelection(assetSelectionInput, { id: selection.id! })
    .then(({ data }) => formatAssetSelectionFromApiOutput(data));
}

/**
 * Create or update an asset selection and save it to the backend
 *
 * @param selection
 */
export function saveAssetSelection(selection: AssetSelection): Promise<string> {
  let result;
  const assetSelectionInput: AssetSelectionInputV1 = formatAssetSelectionToApiInput(selection);
  const isNew = !selection.id;
  const onlyNameChange = _.isEqual(
    _.omit(sqReportStore.getAssetSelectionById(selection.id!), ['name']),
    _.omit(selection, ['name']),
  );
  const onlyDepthChange = _.isEqual(
    _.omit(sqReportStore.getAssetSelectionById(selection.id!), ['assetPathDepth']),
    _.omit(selection, ['assetPathDepth']),
  );
  const worksheetId = sqReportStore.sandboxMode.enabled
    ? sqReportStore.sandboxMode.sandboxedWorksheetId
    : sqWorkbenchStore.stateParams.worksheetId;

  if (isNew) {
    result = sqContentApi.createAssetSelection(assetSelectionInput).then(({ data }) => {
      const assetSelection = formatAssetSelectionFromApiOutput(data);
      setAssetSelection(assetSelection);
      return assetSelection.id!;
    });
  } else {
    result = exposedForTesting.updateAssetSelection(selection).then((assetSelection) => {
      setAssetSelection(assetSelection);
      if (onlyNameChange || onlyDepthChange) {
        // Dont re-render associated content, wait until a new change
        return selection.id!;
      } else {
        forceRefreshContentUsingAssetSelection(assetSelection.id!);
        return assetSelection.id!;
      }
    });
  }
  return result
    .then((assetSelectionId) => {
      if (onlyNameChange || onlyDepthChange) {
        return assetSelectionId;
      }
      return (
        _.chain(sqReportStore.contentUsingAssetSelection(assetSelectionId))
          .filter((content) => !!content.dateRangeId)
          .map((content) => sqReportStore.getDateRangeById(content.dateRangeId!))
          .filter((dateRange): dateRange is DateRange => !!dateRange?.condition?.id)
          .uniqBy('id')
          .map((dateRange) =>
            getDependencies({ id: dateRange.condition!.id })
              // only add date ranges with a condition that has 1 asset dependency
              .then(({ assets }) => (assets.length !== 1 ? undefined : { ...dateRange, dateRangeAsset: assets[0] })),
          )
          .thru((promises) => Promise.all(promises))
          .value()
          .then(_.compact)
          // If one of the potential targets is already on the target asset, then we already have a one to one
          // match, no need to continue, return empty array
          .then((dateRanges) =>
            _.some(dateRanges, (dateRange) => dateRange.dateRangeAsset.id === selection.asset.id) ? [] : dateRanges,
          )
          .then((dateRanges) =>
            _.chain(dateRanges)
              .map((dateRange) =>
                sqItemsApi
                  .findSwap(
                    [
                      {
                        swapIn: selection.asset.id,
                        swapOut: dateRange.dateRangeAsset.id,
                      },
                    ],
                    { id: dateRange.condition!.id },
                  )
                  .then(({ data: item }) => ({
                    ...dateRange,
                    swapItem: item,
                  })),
              )
              .thru((promises) => Promise.all(promises))
              .value(),
          )
          .then((dateRanges) => _.filter(dateRanges, (dateRange) => !!dateRange.swapItem))
          .then((dateRanges) => {
            if (dateRanges.length > 1) {
              if (exposedForTesting.isAdvancedDateRangeSwapEnabled()) {
                const potentialSwaps: DateRangeSwapInfo[] = _.map(dateRanges, (dateRange) => ({
                  dateRange: dateRange as DateRange,
                  swapItem: dateRange.swapItem,
                  swapAsset: dateRange.dateRangeAsset,
                  assetSelection: selection,
                }));
                exposedForTesting.setActiveDateRangeSwapInfo(undefined, potentialSwaps);
                exposedForTesting.setShowChooseAssetSwapModal(true);
              } else {
                doTrack('DateRange Asset Swap', 'Advanced Swap', 'Attempted');
                warnToast(
                  {
                    messageKey: 'REPORT.MODAL.DATE_RANGE_ASSET_SWAP.ADVANCED_NOT_SUPPORTED_MESSAGE',
                  },
                  { autoClose: 30_000 },
                );
              }
              return assetSelectionId;
            } else if (dateRanges.length === 0) {
              // do nothing
              return assetSelectionId;
            } else {
              // There is only one eligible date range
              const dateRange = dateRanges[0];
              exposedForTesting.setActiveDateRangeSwapInfo(
                {
                  dateRange: dateRange as DateRange,
                  swapItem: dateRange.swapItem,
                  swapAsset: dateRange.dateRangeAsset,
                  assetSelection: selection,
                },
                [],
              );
              exposedForTesting.setShowChooseCapsuleModal(true);
              return assetSelectionId;
            }
          })
          .then(() => {
            const property = isNew ? 'inserted' : 'updated';
            emitReportWithSpecificUpdates(worksheetId!, {
              assetSelectionIds: { [property]: [assetSelectionId] },
            });
            return assetSelectionId;
          })
      );
    })
    .catch((err) => {
      errorToast({ httpResponseOrError: err });
      forceRefreshContentUsingAssetSelection(selection.id!);
    }) as Promise<string>;
}

/**
 * Removes the asset selection from the report. Content that uses the selection goes back to inheriting its asset from
 * the worksheet.
 *
 * @returns Promise that resolves once the selection has been archived
 */
export function removeAssetSelection(assetSelection: AssetSelection) {
  return exposedForTesting
    .saveAssetSelection({ ...assetSelection, isArchived: true })
    .then(() => {
      const contentToUpdate = sqReportStore.contentUsingAssetSelection(assetSelection.id!);
      // Remove assetSelection from any content previously using it
      return _.chain(contentToUpdate)
        .map((content) => exposedForTesting.saveContent(_.omit(content, 'assetSelectionId') as Content, true))
        .thru((p) => Promise.all(p))
        .value()
        .then(() => contentToUpdate);
    })
    .then((contentToUpdate) => _.forEach(contentToUpdate, (content) => replaceContentIfExists(content.id!)));
}

export function updateDateRange(dateRange: DateRange): Promise<DateRange> {
  const dateRangeInput: DateRangeInputV1 = formatDateRangeToApiInput(dateRange);
  return sqContentApi
    .updateDateRange(dateRangeInput, { id: dateRange.id })
    .then(({ data }) => formatDateRangeFromApiOutput(data));
}

/**
 * Adds or updates the dateRange on the backend and caches it in the report store. All content using the dateRange
 * is automatically updated either by stepping to now if it is an auto-updating date range or refreshing the
 * content if it is fixed.
 *
 * @param {Object} dateRange - dateRange to set
 * @returns {Promise} that resolves with the ID of the dateRange when it and any modified content has been saved
 */
export function saveDateRange(dateRange: DateRange): Promise<string> {
  let result;
  const dateRangeInput: DateRangeInputV1 = formatDateRangeToApiInput(dateRange);
  const isNewDateRange = !dateRange.id;
  const worksheetId = sqReportStore.sandboxMode.enabled
    ? sqReportStore.sandboxMode.sandboxedWorksheetId
    : sqWorkbenchStore.stateParams.worksheetId;

  if (isNewDateRange) {
    result = sqContentApi.createDateRange(dateRangeInput).then(({ data }) => {
      const dateRange = formatDateRangeFromApiOutput(data);
      const dateRangeId = dateRange.id;
      setDateRange(dateRange);
      return dateRangeId;
    });
  } else {
    const onlyNameChange = _.isEqual(
      _.omit(sqReportStore.getDateRangeById(dateRange.id), ['name']),
      _.omit(dateRange, ['name']),
    );
    result = exposedForTesting.updateDateRange(dateRange).then((dateRange) => {
      setDateRange(dateRange);
      if (onlyNameChange) {
        // Dont re-render associated content, wait until a new change
        return dateRange.id;
      } else if (dateRange.auto.enabled && sqReportStore.hasReportSchedule && sqReportStore.isScheduleEnabled) {
        return exposedForTesting.stepScheduledReportToNow().then(() => dateRange.id);
      } else {
        forceRefreshContentUsingDate(dateRange.id);
        return dateRange.id;
      }
    });
  }

  return result
    .then((dateRangeId) => {
      const property = isNewDateRange ? 'inserted' : 'updated';
      emitReportWithSpecificUpdates(worksheetId!, {
        dateRangeIds: { [property]: [dateRangeId] },
      });

      const maybeRemoveReportSchedulePromise =
        !sqReportStore.hasAutoDateRanges && sqReportStore.hasReportSchedule
          ? exposedForTesting.saveReportSchedule(undefined)
          : Promise.resolve({});
      return maybeRemoveReportSchedulePromise.then(() => dateRangeId);
    })
    .catch((err) => {
      errorToast({ httpResponseOrError: err });
      refreshContentUsingDate(dateRange.id);
    }) as Promise<string>;
}

/**
 * Updates or adds the Seeq content to the report store
 *
 * @param {DateRange} dateRange - DateRange to add/update
 */
export function setDateRange(dateRange: DateRange) {
  flux.dispatch('REPORT_SET_DATE_RANGE', dateRange, PUSH_IGNORE);
}

/**
 * Removes the date range from the report. Content that uses the date range goes back to inheriting its date from
 * the worksheet.
 *
 * @param {Object} dateRange - The date variable to remove.
 * @param {String} dateRange.id - A unique identifier for the variable
 * @returns {Promise} that resolves when date range has been archived
 */
export function removeDateRange(dateRange: DateRange) {
  const contentToUpdate = sqReportStore.contentUsingDateRange(dateRange.id);

  return (
    _.chain(contentToUpdate)
      .map((content) => exposedForTesting.saveContent(_.omit(content, 'dateRangeId') as Content, true))
      .thru((p) => Promise.all(p))
      .value()
      .then(() => exposedForTesting.saveDateRange({ ...dateRange, isArchived: true }))
      // Remove dateRange from any content previously using it
      .then(() => _.forEach(contentToUpdate, (content) => replaceContentIfExists(content.id!)))
  );
}

/**
 * Similar to sqDurationActions.*Range.stepToEnd(), this export function moves the date range contained within in the
 * date range variable such that the end is at the current time. It also takes care of updating the calculated
 * capsule if that capsule has changed
 *
 * @param {string} dateRangeId - ID of the dateRange to update
 * @returns {Promise} resolves when the date range has been updated
 */
export function stepDateRangeToEnd(dateRangeId: string) {
  const dateRange = sqReportStore.getDateRangeById(dateRangeId);
  if (!dateRange) {
    return Promise.reject(`Date range with id ${dateRangeId} not found.`);
  }
  if (dateRange.auto.enabled) {
    return Promise.reject(
      `Can't step individual date range, ${dateRange.name} (${dateRange.id}), to end because it has a live range`,
    );
  }
  if (!dateRange.range) {
    return Promise.reject(`Can't step date range, ${dateRange.name} (${dateRange.id}), to end because it has no range`);
  }

  const now = moment().utc().valueOf();

  if (!_.get(dateRange.condition, 'id', false)) {
    const duration = moment.duration(dateRange.range.end - dateRange.range.start);
    const range = {
      start: moment.utc(now).subtract(duration).valueOf(),
      end: moment.utc(now).valueOf(),
    };

    return exposedForTesting.saveDateRange({
      ...dateRange,
      range: {
        ...dateRange.range,
        ...range,
      },
    });
  }

  let duration;
  const searchRangeEnd = _.get(dateRange.condition, 'range.end');
  const searchRangeStart = _.get(dateRange.condition, 'range.start');
  if (searchRangeStart && searchRangeEnd) {
    duration = moment.duration(searchRangeEnd - searchRangeStart);
  } else {
    duration = moment.duration(dateRange.range.end - dateRange.range.start);
  }
  const range = {
    start: moment.utc(now).subtract(duration).valueOf(),
    end: moment.utc(now).valueOf(),
  };

  const offset = computeCapsuleOffset(dateRange.condition!);
  return exposedForTesting.computeCapsule(dateRange.condition!, range, offset).then((capsule) =>
    exposedForTesting.saveDateRange({
      ...dateRange,
      condition: {
        ...dateRange.condition!,
        range,
      },
      range: {
        ...dateRange.range,
        start: capsule.start / NUMBER_CONVERSIONS.NANOSECONDS_PER_MILLISECOND,
        end: capsule.end / NUMBER_CONVERSIONS.NANOSECONDS_PER_MILLISECOND,
      },
    }),
  );
}

/**
 * Update `now` for a scheduled report (also stepping the scheduled date ranges to now)
 * @returns {Promise} resolves when the report has been updated
 */
export function stepScheduledReportToNow(store: ReportStore = sqReportStore): Promise<any> {
  return _.chain(store.dateRangesNotArchived)
    .filter('auto.enabled')
    .forEach((dateRange) => refreshContentUsingDate(dateRange.id))
    .thru((autoDateRanges) =>
      autoDateRanges.length
        ? exposedForTesting.updateReport(store.id!, getHtmlReportEditor(), true)
        : Promise.resolve({}),
    )
    .value();
}

/**
 * Determines how many capsule are present in a condition for the given time range.
 *
 * @param {Object} condition - The condition to evaluate
 * @param {String} condition.id - The id of the condition
 * @param {Range} range - The date range in which to find capsules
 * @return {Promise} Resolves with the count of capsules
 */
export function computeCapsuleCount(condition: DateRangeCondition, range: Range) {
  const { id } = condition;
  const start = moment.utc(range.start).toISOString();
  const end = moment.utc(range.end).toISOString();
  const formula = `$condition${getMaximumDurationFormula(condition)}.count(capsule('${start}','${end}'))`;
  const parameters = { condition: id };

  return computeScalar({ formula, parameters }).then(({ value }) => value);
}

/**
 * Determines the capsule to be used for a live screenshot given a condition and time range.
 *
 * @param {Object} condition - The condition to evaluate
 * @param {String} condition.id - The id of the condition
 * @param {Range} range - The date range in which to find capsules
 * @param {Number} offset - Which capsule to pick from the group
 *
 * @return {Promise} Resolves with the capsule or rejects if there are none
 */
export function computeCapsule(condition: DateRangeCondition, range: Range, offset: number) {
  const { id } = condition;
  const start = moment.utc(range.start).toISOString();
  const end = moment.utc(range.end).toISOString();
  const formula = `$condition.setCertain()${getMaximumDurationFormula(
    condition,
  )}.toGroup(capsule('${start}','${end}')).pick(${offset})`;

  const parameters = { condition: id };

  return computeCapsules({ formula, parameters }).then((response) =>
    _.size(response.capsules) > 0 ? response.capsules[0] : Promise.reject(),
  );
}

/**
 * Returns the formula segment for the maximum duration
 * This should be added for all unbounded conditions, as a max duration is required for topics
 *
 * @param {Object} condition - The condition housing the maximum duration data
 */
export function getMaximumDurationFormula(condition: any) {
  const hasMaxDuration = { ...condition.maximumDuration };
  return !_.isEmpty(hasMaxDuration) ? `.removeLongerThan(${hasMaxDuration.value}${hasMaxDuration.units})` : '';
}

/**
 * Save a new comment
 *
 * @param  {String} reportId - ID of the journal entry to which this is a comment
 * @param  {String} name - the text of the comment
 * @return {Promise} Promise which is resolved when the comment is saved
 */
export function addComment(reportId: string, name: string) {
  const worksheetId = sqWorkbenchStore.stateParams.worksheetId;
  return sqAnnotationsApi.createAnnotation({ repliesTo: reportId, name }).then(({ data: comment }) => {
    flux.dispatch('REPORT_SET_COMMENT', comment, PUSH_IGNORE);
    exposedForTesting.emitReportWithSpecificUpdates(worksheetId, {
      commentIds: { inserted: [comment.id] },
    });
  });
}

/**
 * Update an existing comment
 *
 * @param  {String} commentId - the ID of the comment to update
 * @param {String} name - the text of the comment
 * @return {Promise} Promise which is resolved when the comment is updated
 */
export function updateComment(commentId: string, name: string) {
  const worksheetId = sqWorkbenchStore.stateParams.worksheetId;
  return sqAnnotationsApi.updateAnnotation({ name }, { id: commentId }).then(({ data: comment }) => {
    flux.dispatch('REPORT_SET_COMMENT', comment, PUSH_IGNORE);
    exposedForTesting.emitReportWithSpecificUpdates(worksheetId, {
      commentIds: { updated: [commentId] },
    });
  });
}

/**
 * Delete an existing comment
 *
 * @param  {String} commentId - the ID of the comment to delete
 * @returns {Promise} a promise that resolves when comments have been retrieved and set in the store
 */
export function deleteComment(commentId: string) {
  const worksheetId = sqWorkbenchStore.stateParams.worksheetId;
  return sqAnnotationsApi.archiveAnnotation({ id: commentId }).then(() => {
    flux.dispatch('REPORT_REMOVE_COMMENT', commentId, PUSH_IGNORE);
    exposedForTesting.emitReportWithSpecificUpdates(worksheetId, {
      commentIds: { removed: [commentId] },
    });
  });
}

/**
 * Fetch the data for the report view. Used when rehydrating
 *
 * @returns {Promise} A promise that resolves when all the data is fetched.
 */
export function fetchReport() {
  if (!sqReportStore.id) {
    return Promise.resolve({});
  }

  return extraExposedForTesting.loadReport(sqReportStore.id);
}

/**
 * Fetch a comment for the report and sets it in the store
 *
 * @returns {Promise} A promise that resolves when all the data is fetched.
 */
export function fetchComment(id: string) {
  return sqAnnotationsApi.getAnnotation({ id }).then(({ data: comment }) => {
    if (comment.type !== 'Reply') throw new Error("'id' does not correspond to a comment");
    flux.dispatch('REPORT_SET_COMMENT', comment, PUSH_IGNORE);
  });
}

/**
 * Synchronizes store when other users remove date ranges
 *
 * @param {ReportUpdateMessageWithSpecificUpdates} message containing ids of changes
 * @return {Promise} that is immediately resolved
 */
export function onReportSyncStoreDateRangeRemoval(message: ReportUpdateMessageWithSpecificUpdates): Promise<any> {
  if (!message || !message.dateRangeIds?.removed) return Promise.resolve();
  const { dateRangeIds } = message;

  dateRangeIds.removed?.forEach?.((dateRangeId) => {
    // Remove date range from content prior to removing the date range
    const contentUsingDateRange = sqReportStore.contentUsingDateRange(dateRangeId);
    contentUsingDateRange.forEach((content: Content) => {
      const contentWithoutDateRange = _.omit(content, 'dateRangeId');
      flux.dispatch('REPORT_SET_CONTENT', contentWithoutDateRange, PUSH_IGNORE);
      replaceContentIfExists(content.id!);
    });
    flux.dispatch(
      'REPORT_SET_DATE_RANGE',
      { ...sqReportStore.getDateRangeById(dateRangeId), isArchived: true },
      PUSH_IGNORE,
    );
  });

  return Promise.resolve();
}

/**
 * Synchronizes store when other users add date ranges
 *
 * @param {ReportUpdateMessageWithSpecificUpdates} message containing ids of changes
 * @return {Promise} that is resolved when the dateRange has been fetched and any content updated in the store
 */
export function onReportSyncStoreDateRangeInsertion(message: ReportUpdateMessageWithSpecificUpdates): Promise<any> {
  // By the time we've received the notification, the date range and content relationships should be up to date on
  // the server.  So, we'll use that response to sync the content of our store.
  if (!message || !message.dateRangeIds?.inserted) return Promise.resolve();
  const { dateRangeIds } = message;

  return _.chain(dateRangeIds.inserted)
    .map((dateRangeId) => {
      return exposedForTesting.fetchDateRange(dateRangeId).then(({ contentIds: contentIdsUsingDateRange }) => {
        contentIdsUsingDateRange.forEach((contentId) => {
          const content = {
            ...sqReportStore.getContentById(contentId!),
            dateRangeId,
          };
          flux.dispatch('REPORT_SET_CONTENT', content, PUSH_IGNORE);
        });
      });
    })
    .thru((p) => Promise.all(p))
    .value();
}

/**
 * Synchronizes store when other users update date ranges
 *
 * @param {ReportUpdateMessageWithSpecificUpdates} message containing ids of changes
 * @return {Promise} that is resolved when all specified date ranges have been fetched
 */
export function onReportSyncStoreDateRangeUpdate(message: ReportUpdateMessageWithSpecificUpdates): Promise<any> {
  // Date range updates are isolated. Content and report are not affected, so we only need to update the store's
  // date range
  if (!message || !message.dateRangeIds?.updated) return Promise.resolve();
  const { dateRangeIds } = message;

  return _.chain(dateRangeIds.updated)
    .map((dateRangeId) => exposedForTesting.fetchDateRange(dateRangeId))
    .thru((p) => Promise.all(p))
    .value();
}

/**
 * Synchronizes store when other users update content
 *
 * @param {ReportUpdateMessageWithSpecificUpdates} message containing ids of changes
 * @return {Promise} that is resolved when all content specified has been fetched
 */
export function onReportSyncStoreContentUpdate(message: ReportUpdateMessageWithSpecificUpdates): Promise<any> {
  if (!message || !message.contentIds?.updated) return Promise.resolve();
  const { contentIds } = message;
  return exposedForTesting.fetchMultipleContent(contentIds.updated);
}

/**
 * Synchronizes store when other users remove comments
 *
 * @param {ReportUpdateMessageWithSpecificUpdates} message containing ids of changes
 * @return {Promise} that is immediately resolved
 */
export function onReportSyncStoreCommentRemoval(message: ReportUpdateMessageWithSpecificUpdates): Promise<any> {
  if (!message || !message.commentIds?.removed) return Promise.resolve();
  const { commentIds } = message;
  commentIds.removed?.forEach?.((commentId) => flux.dispatch('REPORT_REMOVE_COMMENT', commentId, PUSH_IGNORE));
  return Promise.resolve();
}

/**
 * Synchronizes store when other users add comments
 *
 * @param {ReportUpdateMessageWithSpecificUpdates} message containing ids of changes
 * @return {Promise} Promise which is resolved when the comment is inserted
 */
export function onReportSyncStoreCommentInsertion(message: ReportUpdateMessageWithSpecificUpdates): Promise<any> {
  if (!message || !message.commentIds?.inserted) return Promise.resolve();
  const { commentIds } = message;
  return _.chain(commentIds.inserted)
    .map((commentId) => exposedForTesting.fetchComment(commentId))
    .thru((p) => Promise.all(p))
    .value();
}

/**
 * Synchronizes store when other users update comments
 *
 * @param {ReportUpdateMessageWithSpecificUpdates} message containing ids of changes
 * @return {Promise} Promise which is resolved when the comment is updated
 */
export function onReportSyncStoreCommentUpdate(message: ReportUpdateMessageWithSpecificUpdates): Promise<any> {
  if (!message || !message.commentIds?.updated) return Promise.resolve();
  const { commentIds } = message;
  return _.chain(commentIds.updated)
    .map((commentId) => exposedForTesting.fetchComment(commentId))
    .thru((p) => Promise.all(p))
    .value();
}

/**
 * Synchronizes store when other users add asset selections
 *
 * @param {ReportUpdateMessageWithSpecificUpdates} message containing ids of changes
 * @return {Promise} that is resolved when the asset selection has been fetched and any content updated in the store
 */
export function onReportSyncStoreAssetSelectionInsertion(
  message: ReportUpdateMessageWithSpecificUpdates,
): Promise<any> {
  // By the time we've received the notification, the asset selection and content relationships should be up to
  // date on the server.  So, we'll use that response to sync the content of our store.
  if (!message || !message.assetSelectionIds?.inserted) return Promise.resolve();
  const { assetSelectionIds } = message;

  return _.chain(assetSelectionIds.inserted)
    .map((assetSelectionId) =>
      exposedForTesting.fetchAssetSelection(assetSelectionId).then(({ contentIds: contentIdsUsingAssetSelections }) => {
        contentIdsUsingAssetSelections.forEach((contentId) => {
          const content = {
            ...sqReportStore.getContentById(contentId),
            assetSelectionId,
          };
          flux.dispatch('REPORT_SET_CONTENT', content, PUSH_IGNORE);
        });
      }),
    )
    .thru((p) => Promise.all(p))
    .value();
}

/**
 * Fetches a single asset selection used in the current report, populating in sqReportStore.
 *
 * @param  id - ID of selection to fetch
 * @param populateStore - true to populate fetched assetSelection in report store
 * @returns {Promise} that resolves when the asset selection has been retrieved.
 */
export function fetchAssetSelection(
  id: string,
  populateStore = true,
): Promise<{ assetSelection: AssetSelection; contentIds: string[] }> {
  return sqContentApi.getAssetSelection({ id }).then(({ data }) => {
    const assetSelection = formatAssetSelectionFromApiOutput(data);
    if (populateStore) {
      flux.dispatch('REPORT_SET_ASSET_SELECTION', assetSelection, PUSH_IGNORE);
    }
    return {
      assetSelection,
      contentIds: data?.content?.map?.((contentItemPreview) => contentItemPreview.id) ?? [],
    };
  });
}

/**
 * Synchronizes store when other users update asset selections
 *
 * @param {ReportUpdateMessageWithSpecificUpdates} message containing ids of changes
 * @return {Promise} that is resolved when all specified selections have been fetched
 */
export function onReportSyncStoreAssetSelectionUpdate(message: ReportUpdateMessageWithSpecificUpdates): Promise<any> {
  if (!message || !message.assetSelectionIds?.updated) return Promise.resolve();
  const { assetSelectionIds } = message;

  return _.chain(assetSelectionIds.updated)
    .map((selectionId) => exposedForTesting.fetchAssetSelection(selectionId))
    .thru((p) => Promise.all(p))
    .value();
}

/**
 * Synchronizes the store by processing all of the received changes
 *
 * @param {ReportUpdateMessage} reportUpdateMessage containing type of message and possibly ids of changes
 * @return {Promise} that resolves when the associated actions have been completed
 */
export function onReportSyncStore(reportUpdateMessage: ReportUpdateMessage): Promise<ReportUpdateMessage> {
  // From the perspective of our store:
  //  Document Change: For now, we update everything in the store from the server since there's a lot to worry
  //  about -- comments, backups, etc, but we can get more selective as needed. An alternative is to pass the
  //  whole store from the sender and use that.  There is room for optimization by doing a smart diff of the
  //  document to determine what needs to be updated
  //
  //  Content or date range modifications: Update only what has changed
  //
  // From the perspective of listeners:
  //  There are components that listen to the store, and it may or may not make a significant
  //  difference to selectively set the store content since the listeners will get a notification
  //  for any change.

  const { type, message } = reportUpdateMessage;
  if (type === ReportUpdateMessageType.FULL_UPDATE) {
    return Promise.resolve(exposedForTesting.fetchReport()).then((_report) => reportUpdateMessage);
  } else if (type === ReportUpdateMessageType.SPECIFIC_UPDATES_ONLY) {
    if (!message) return Promise.resolve(reportUpdateMessage);
    return exposedForTesting
      .onReportSyncStoreDateRangeRemoval(message)
      .then(() => exposedForTesting.onReportSyncStoreDateRangeInsertion(message))
      .then(() => exposedForTesting.onReportSyncStoreDateRangeUpdate(message))
      .then(() => exposedForTesting.onReportSyncStoreContentUpdate(message))
      .then(() => exposedForTesting.onReportSyncStoreAssetSelectionInsertion(message))
      .then(() => exposedForTesting.onReportSyncStoreAssetSelectionUpdate(message))
      .then(() => exposedForTesting.onReportSyncStoreCommentRemoval(message))
      .then(() => exposedForTesting.onReportSyncStoreCommentInsertion(message))
      .then(() => exposedForTesting.onReportSyncStoreCommentUpdate(message))
      .then(() => reportUpdateMessage);
  } else {
    throw new Error('Invalid message type: Unable to parse the report update message');
  }
}

/**
 * Synchronizes the view by processing all of the received changes
 *
 * @param {ReportUpdateMessage} reportUpdateMessage containing type of message and possibly ids of changes
 */
export function onReportSyncView(reportUpdateMessage: ReportUpdateMessage) {
  const { type, message } = reportUpdateMessage;
  if (type === ReportUpdateMessageType.FULL_UPDATE) {
    const document = sqReportStore.document!;
    exposedForTesting.setReportView(document);
  } else if (type === ReportUpdateMessageType.SPECIFIC_UPDATES_ONLY) {
    // Otherwise, refresh just the content that has been affected
    const { contentIds, dateRangeIds, assetSelectionIds } = message as ReportUpdateMessageWithSpecificUpdates;
    dateRangeIds?.inserted?.forEach?.((dateRangeId) => refreshContentUsingDate(dateRangeId));
    dateRangeIds?.updated?.forEach?.((dateRangeId) => refreshContentUsingDate(dateRangeId));
    assetSelectionIds?.inserted?.forEach((assetSelectionId) =>
      forceRefreshContentUsingAssetSelection(assetSelectionId),
    );
    assetSelectionIds?.updated?.forEach((assetSelectionId) => forceRefreshContentUsingAssetSelection(assetSelectionId));
    contentIds?.updated?.forEach?.((contentId) => replaceContentIfExists(contentId));
  } else {
    throw new Error('Invalid message type: Unable to parse the report update message');
  }
}

/**
 * Handles report update messages.
 *
 * @param data the data describing the report changes
 * @returns {Promise} that resolves when report has been loaded
 */
export async function onReport({ data }: { data: ReportUpdateMessage }): Promise<any> {
  if (sqReportStore.sandboxMode.enabled && !data.message?.skipSandboxHandling) {
    // If the user hasn't modified anything then keep it in sync with the original report
    if (!sqReportStore.sandboxMode.isModified) {
      await extraExposedForTesting.loadReport(sqReportStore.sandboxMode.originalReportId!);
    } else {
      infoToast({
        messageKey: 'RELOAD_MESSAGE_TOPIC',
        buttonLabelKey: 'RELOAD',
        buttonAction: () => window.location.reload(),
        toastUUID: 'sandboxModeReload',
      });
    }
    await exposedForTesting.onReportSyncStoreCommentRemoval(data.message!);
    await exposedForTesting.onReportSyncStoreCommentInsertion(data.message!);
    await exposedForTesting.onReportSyncStoreCommentUpdate(data.message!);
  } else {
    await exposedForTesting.onReportSyncStore(data);
    exposedForTesting.onReportSyncView(data);
  }
}

/**
 * Sets the report view to the specified document
 *
 * @param {string} document - updated html document received
 */
export function setReportView(document: string) {
  if (canModifyDocument()) {
    const scrollOffset = getScrollOffset();
    const maybePosition = getCursorPosition('reportEditor');
    setHtmlCKEditor(document, 'reportEditor');
    setScrollOffset(scrollOffset);
    maybePosition && setCursorPosition(maybePosition, 'reportEditor');
  } else {
    setHtmlCKEditor(document, 'reportEditor');
  }
}

/**
 * Emits a report update message.
 */
export function emitReportWithSpecificUpdates(
  worksheetId: string,
  reportUpdateMessage: ReportUpdateMessageWithSpecificUpdates,
) {
  emit([SeeqNames.Channels.ReportUpdateChannel, worksheetId], {
    type: ReportUpdateMessageType.SPECIFIC_UPDATES_ONLY,
    message: reportUpdateMessage,
  });
}

/**
 * Fetches the backup document property and sets it as the backupPreview property in the report store.
 *
 * @param {Object} backupPreview - object container for backup preview
 * @param {string} backupPreview.backupName - the backup preview property name
 */
export function setBackupPreview(backupPreview: { backupName: string }) {
  sqItemsApi
    .getProperty({
      id: sqReportStore.id!,
      propertyName: backupPreview.backupName,
    })
    .then(({ data }) => {
      flux.dispatch(
        'REPORT_SET_BACKUP_PREVIEW',
        {
          backupPreview: _.merge({}, backupPreview, {
            document: _.get(data, 'value'),
          }),
        },
        PUSH_IGNORE,
      );
    });
}

/**
 * Clears the report store backupPreview property
 */
export function clearBackupPreview() {
  flux.dispatch('REPORT_SET_BACKUP_PREVIEW', {}, PUSH_IGNORE);
}

/**
 * Archives content and date ranges that are not in the backup, and unarchives content and date ranges that are
 * part of the backup. Also updates the store
 *
 * NOTE: There is a known deficiency where the date range may not be restored if the content has a history of
 * having its associated date range changed
 *
 * @param {string} backupDocument - HTML document contents to parse
 * @returns {Promise} A promise that resolves after all server calls are made
 */
export function syncServerAndStoreToBackup(backupDocument: string): Promise<any> {
  // Ensure that all content in backup is restored
  const backupContentIds = exposedForTesting.parseSeeqContentIdsFromHtml(backupDocument);
  const currentContentIds = exposedForTesting.parseSeeqContentIdsFromHtml(sqReportStore.document!);

  const contentIdsToRemove = _.difference(currentContentIds, backupContentIds);
  const contentIdsToRestore = _.difference(backupContentIds, currentContentIds);

  return _.chain(contentIdsToRemove)
    .map((contentId) => exposedForTesting.removeContent(sqReportStore.getContentById(contentId)!))
    .thru((p) => Promise.all(p))
    .value()
    .then(() =>
      _.chain(contentIdsToRestore)
        .map((contentId) => exposedForTesting.restoreContent(contentId))
        .thru((p) => Promise.all(p))
        .value(),
    );
}

/**
 * If the report store backupPreview property is defined, then tell the backend to restore the report annotation to
 * match the state of the backupName backup and set it as the current document.
 */
export function restoreBackup() {
  const { backupPreview } = sqReportStore;
  if (!backupPreview) return;

  const workbookId = sqWorkbenchStore.stateParams.workbookId;
  const worksheetId = sqWorkbenchStore.stateParams.worksheetId;
  const backupPreviewDocument = backupPreview.document;
  const { description, name } = nameAndDescriptionFromDocument(backupPreviewDocument);

  return exposedForTesting
    .syncServerAndStoreToBackup(backupPreviewDocument)
    .then(() =>
      sqAnnotationsApi.updateAnnotation(
        {
          name,
          description,
          type: API_TYPES.REPORT,
          backupName: backupPreview.backupName,
          reportInput: {
            cronSchedule: sqReportStore.reportSchedule?.cronSchedule,
            background: !!sqReportStore.reportSchedule?.background,
            enabled: !!sqReportStore.reportSchedule?.enabled,
          },
        },
        { id: sqReportStore.id! },
      ),
    )
    .then(() => {
      // Clear the backup preview so the report editor will be displayed by ng-if
      exposedForTesting.clearBackupPreview();

      // Give the report editor time to be displayed in the DOM before restoring the backup
      setTimeout(() => {
        // Ensure the current version is in the undo buffer before setting the restored version
        setHtmlCKEditor(sqReportStore.document!, 'reportEditor');
        setHtmlCKEditor(backupPreviewDocument, 'reportEditor');
      }, NG_IF_WAIT);
    });
}

/**
 * Set whether or not we're in the process of updating date ranges
 *
 * @param dateRangeUpdating - true if a date range is being updated, false otherwise
 */
export function setDateRangeUpdating(dateRangeUpdating: boolean) {
  flux.dispatch('REPORT_SET_DATE_RANGE_UPDATING', { dateRangeUpdating });
}

/**
 * Update the start and end times for the given date range.
 * @param {String} dateRangeId
 * @param {Moment} start
 * @param {Moment} end
 */
export function updateDateRangeStartAndEnd(dateRangeId: string, start: number, end: number) {
  flux.dispatch('REPORT_UPDATE_RANGE_START_AND_END', {
    dateRangeId,
    start,
    end,
  });
}

/**
 * Sets the given date range's "no capsule found" flag to the desired value (true, by default)
 * @param {String} dateRangeId
 * @param {Boolean} value
 */
export function setNoCapsuleFound(dateRangeId: string, value = true) {
  flux.dispatch('REPORT_UPDATE_NO_CAPSULE_FOUND', { dateRangeId, value });
}

/**
 * Sets the display mode for the bulk edit modal (INIT, ASSET_SELECTION, DATE_RANGE, or ERROR)
 * @param {BulkEditMode} bulkEditDisplayMode
 */
export function setBulkEditDisplayMode(bulkEditDisplayMode: BulkEditMode) {
  flux.dispatch('REPORT_SET_BULK_EDIT_DISPLAY_MODE', { bulkEditDisplayMode }, PUSH_IGNORE);
}

/**
 * Sets the interactive status to use for bulk editing
 * @param {Object} bulkInteractive
 */
export function setBulkInteractive(bulkInteractive: InteractiveReportContent | undefined) {
  flux.dispatch('REPORT_SET_BULK_INTERACTIVE', { bulkInteractive }, PUSH_IGNORE);
}

/**
 * Sets the shape to use for bulk editing
 * @param {Object} bulkShape
 */
export function setBulkShape(bulkShape: ReportContentShape | undefined) {
  flux.dispatch('REPORT_SET_BULK_SHAPE', { bulkShape }, PUSH_IGNORE);
}

/**
 * Sets the scale to use for bulk editing
 * @param {Object} bulkScale
 */
export function setBulkScale(bulkScale: ReportContentScale | undefined) {
  flux.dispatch('REPORT_SET_BULK_SCALE', { bulkScale }, PUSH_IGNORE);
}

/**
 * Sets the size to use for bulk editing
 * @param {Object} bulkSize
 */
export function setBulkSize(bulkSize: ReportContentSize) {
  flux.dispatch('REPORT_SET_BULK_SIZE', { bulkSize }, PUSH_IGNORE);
}

/**
 * Sets the width to use for bulk editing
 * @param {Object} bulkWidth
 */
export function setBulkWidth(bulkWidth: number | string) {
  flux.dispatch('REPORT_SET_BULK_WIDTH', { bulkWidth }, PUSH_IGNORE);
}

/**
 * Sets the height to use for bulk editing
 * @param {Object} bulkHeight
 */
export function setBulkHeight(bulkHeight: number | string) {
  flux.dispatch('REPORT_SET_BULK_HEIGHT', { bulkHeight }, PUSH_IGNORE);
}

/**
 * Sets the date range to use for bulk editing
 * @param {Object} bulkDateRange
 */
export function setBulkDateRange(bulkDateRange: Pick<DateRange, 'id' | 'name'> | undefined) {
  flux.dispatch('REPORT_SET_BULK_DATE_RANGE', { bulkDateRange }, PUSH_IGNORE);
}

/**
 * Sets the summary to use for bulk editing
 * @param bulkSummary
 */
export function setBulkSummary(bulkSummary: ReportContentSummary) {
  flux.dispatch('REPORT_SET_BULK_SUMMARY', { bulkSummary }, PUSH_IGNORE);
}

/**
 * Sets if uncertainty should be hidden for bulk editing
 * @param bulkHideUncertainty
 */
export function setBulkHideUncertainty(bulkHideUncertainty: boolean | undefined | null) {
  flux.dispatch('REPORT_SET_BULK_HIDE_UNCERTAINTY', { bulkHideUncertainty }, PUSH_IGNORE);
}

/**
 * Sets the AssetSelection to use for bulk edit
 * @param bulkAssetSelection
 */
export function setBulkAssetSelection(bulkAssetSelection?: AssetSelection) {
  flux.dispatch('REPORT_SET_BULK_ASSET_SELECTION', { bulkAssetSelection }, PUSH_IGNORE);
}

/**
 * Sets the content selected for bulk editing
 * @param {Object[]} selectedBulkContent
 */
export function setSelectedBulkContent(selectedBulkContent: DecoratedContent[]) {
  flux.dispatch('REPORT_SET_SELECTED_BULK_CONTENT', { selectedBulkContent }, PUSH_IGNORE);
}

/**
 * Sets whether or not all pieces of content being edited should update their workstep.
 */
export function setShouldUpdateBulkWorkstep(shouldUpdateBulkWorkstep: boolean) {
  flux.dispatch('REPORT_SET_SHOULD_UPDATE_BULK_WORKSTEP', { shouldUpdateBulkWorkstep }, PUSH_IGNORE);
}

/**
 * Clears all bulk properties back to default state
 */
export function clearBulkProperties() {
  flux.dispatch('REPORT_UPDATE_CLEAR_BULK_PROPERTIES', {}, PUSH_IGNORE);
}

/**
 * Marks the passed in content as selected if it is not selected, and does the opposite if it is selected
 *
 * @param {object} content
 */
export function toggleSpecificSelectedContent(content: Content) {
  flux.dispatch('REPORT_TOGGLE_SPECIFIC_SELECTED_CONTENT', content, PUSH_IGNORE);
}

/**
 * Saves schedule on the report, overriding any schedule specified on individual date ranges in this report
 *
 * @returns {Promise} a promise that resolves when the report schedule has been saved
 */
export function saveReportSchedule(reportSchedule: ReportSchedule | undefined): Promise<any> {
  flux.dispatch('REPORT_SET_REPORT_SCHEDULE', reportSchedule, PUSH_IGNORE);
  return updateReport(sqReportStore.id!);
}

/**
 * Updates the timestamp for the last time the report was updated due to a scheduled update
 */
export function incrementScheduledUpdateCount(): void {
  flux.dispatch('REPORT_SCHEDULED_UPDATE_RECEIVED', undefined, PUSH_IGNORE);
}

/**
 * Updates the next scheduled run time
 */
export function updateNextRunTime(reportId: string): Promise<string | undefined> {
  return sqAnnotationsApi
    .getAnnotation({ id: reportId })
    .then(({ data }) => data.nextRunTime)
    .then((nextRunTime) => {
      flux.dispatch('REPORT_SET_NEXT_RUN_TIME', nextRunTime, PUSH_IGNORE);
      return nextRunTime;
    });
}

export function setShouldShowConfigureAutoUpdateModal(shouldShowConfigureAutoUpdateModal: boolean) {
  flux.dispatch('REPORT_SET_SHOULD_SHOW_CONFIGURE_AUTO_UPDATE_MODAL', {
    shouldShowConfigureAutoUpdateModal,
  });
}

/**
 * Set whether or not the auto-update modal should be visible
 *
 * @param showConfigureAutoUpdateModal - true to show the auto update modal
 * @param reportScheduleOverride
 */
export function setShowConfigureAutoUpdateModal(showConfigureAutoUpdateModal: boolean, reportScheduleOverride = false) {
  flux.dispatch('REPORT_SET_SHOW_CONFIGURE_AUTO_UPDATE_MODAL', {
    showConfigureAutoUpdateModal,
    reportScheduleOverride,
  });
}

export function setShowChooseAssetSwapModal(showModal: boolean) {
  flux.dispatch('REPORT_SET_SHOW_ASSET_SWAP_MODAL', showModal);
}

export function setShowChooseCapsuleModal(showModal: boolean) {
  flux.dispatch('REPORT_SET_SHOW_CAPSULE_MODAL', showModal);
}

export function setActiveDateRangeSwapInfo(
  currentSwap: DateRangeSwapInfo | undefined,
  potentialSwaps: DateRangeSwapInfo[],
) {
  flux.dispatch('REPORT_SET_SWAP_RANGE_INFO', {
    currentSwap,
    potentialSwaps,
  });
}

export function setAssetSelection(selection: AssetSelection): void {
  flux.dispatch('REPORT_SET_ASSET_SELECTION', selection);
}

export function setSandboxMode(sandboxMode: SandboxMode): void {
  flux.dispatch('REPORT_SET_SANDBOX_MODE', sandboxMode);
}

/**
 * Toggles whether or not the current report has a fixed width
 */
export function toggleFixedWidth() {
  sqItemsApi.setProperty(
    { value: !sqReportStore.isFixedWidth },
    { id: sqReportStore.id!, propertyName: SeeqNames.Properties.IsFixedWith },
    { ignoreLoadingBar: true },
  );
  flux.dispatch('REPORT_SET_IS_FIXED_WIDTH', {
    isFixedWidth: !sqReportStore.isFixedWidth,
  });
}

/**
 * Used when in view-only or presentation mode so that the user can change things like the asset selection
 * or date range without changing the original. Works by duplicating the current worksheet to a new Topic, which will be
 * in the users home folder, but archived.
 */
const CONTENT_PERMISSIONS_CLONE_ERROR = 'Could not duplicate Content';
export function createSandboxAndLoadTempReport(reportStore = sqReportStore): Promise<void> {
  if (loadingSandboxModePromise) {
    return loadingSandboxModePromise;
  }
  const isReportScheduleActive = reportStore.reportSchedule?.enabled;
  const sandboxOriginalCreatorName = reportStore.createdBy?.name;
  doTrack('Sandbox Mode', 'Activate Sandbox Mode');
  const originalReportId = reportStore.id;
  const originalWorksheetId = sqWorkbenchStore.stateParams.worksheetId;
  const originalWorkbookId = sqWorkbookStore.workbookId;
  const isCurrentUserTrusted = sqWorkbenchStore.currentUser.isTrusted;
  // Restricted / untrusted users should render the topic as the renderer allowing them to change the date range and
  // selected asset
  const useRendererUser = !isCurrentUserTrusted;
  loadingSandboxModePromise = sqWorkbooksApi
    .createSandbox(
      {
        name: t('SANDBOX_MODE.VIEW_ONLY_TOPIC_NAME', { workbook: sqWorkbookStore.name }),
        useRendererUser,
      },
      {
        worksheetId: originalWorksheetId,
        workbookId: originalWorkbookId,
      },
    )
    .then(async (newWorksheetResponse) => {
      const urlParams = new URLSearchParams(window.location.search);
      const transformDataOnLoad =
        urlParams.get('dateRangeStart') || urlParams.get('dateRangeEnd') || urlParams.get('assetSelection')
          ? sandboxUrlParams(
              {
                assetSelections: reportStore.assetSelectionsNotArchived,
                dateRanges: reportStore.dateRangesNotArchived,
              },
              { utilities },
            ).applyUrlParamsToData(urlParams)
          : undefined;

      await extraExposedForTesting.loadReport(newWorksheetResponse.data.report!.id, transformDataOnLoad, true);

      // Content is rendered as the renderer/owner of the user of the topic which allows users to see worksheets
      // (as content) that a viewer of the topic does not have access to. However, when it is cloned the current
      // user must be the renderer and will end up with content clone errors if they don't have access to the
      // worksheets. We don't want the user to see a document with a bunch of errors, so we roll back.
      if (reportStore.document?.includes(CONTENT_PERMISSIONS_CLONE_ERROR)) {
        return extraExposedForTesting.loadReport(originalReportId!, undefined, false, true);
      }

      exposedForTesting.setReportView(reportStore.document!);
      setSandboxMode({
        enabled: true,
        // If renderer user is used for rendering, we mark the sandboxed report as modified so that it does not
        // automatically sync with the original report
        isModified: useRendererUser,
        originalWorksheetId,
        originalReportId: originalReportId!,
        sandboxedWorkbookId: newWorksheetResponse.data.workbook!.id,
        sandboxedWorksheetId: newWorksheetResponse.data.id,
        sandboxOriginalCreatorName,
        useRendererUser,
      });
      if (isReportScheduleActive) {
        const reportSchedule = {
          ...reportStore.reportSchedule,
          enabled: true,
        };
        flux.dispatch('REPORT_SET_REPORT_SCHEDULE', reportSchedule);
        // When a topic is cloned, the report and its date ranges are disabled and the user must manually re-enable.
        // However, in sandbox mode they should automatically be enabled, but it must be done with setProperty so
        // that the cache of the generated content is not invalidated (CRAB-47363).
        const enablePromises = reportStore.dateRanges
          .filter((dr) => dr.auto.enabled)
          .map(({ id }) => id)
          .concat(newWorksheetResponse.data.report!.id)
          .map((id) =>
            sqItemsApi.setProperty(
              { value: true },
              {
                id,
                propertyName: SeeqNames.Properties.Enabled,
              },
            ),
          );
        await Promise.all(enablePromises);
      }
      subscribeToReport(newWorksheetResponse.data.report!.id);
    })
    .finally(() => {
      loadingSandboxModePromise = undefined;
    });
  return loadingSandboxModePromise;
}

export function addContentError(contentId: string) {
  flux.dispatch('REPORT_ADD_CONTENT_ERROR', { contentId });
}

export function resetContentErrors() {
  flux.dispatch('REPORT_RESET_CONTENT_ERRORS');
}

/**
 * Turns the warning for a piece of content on or off
 *
 * @param contentId - If falsy, all content in this worksheet will have showWarningMessage updated
 * @param showWarningMessage - If true, warning message is shown for specified piece of content. If not, the warning
 * message is not shown.
 */
export function setContentShowWarningMessage(contentId: string | null, showWarningMessage: boolean) {
  flux.dispatch('REPORT_SET_CONTENT_SHOW_WARNING_MESSAGE', {
    contentId,
    showWarningMessage,
  });
}

export const exposedForTesting = {
  saveReportSchedule,
  updateAssetSelection,
  createSandboxAndLoadTempReport,
  updateReport,
  emitReportWithSpecificUpdates,
  removeContent,
  restoreContent,
  parseSeeqContentIdsFromHtml,
  fetchContent,
  saveContent,
  saveDateRange,
  setActiveDateRangeSwapInfo,
  setShowChooseCapsuleModal,
  setShowChooseAssetSwapModal,
  isAdvancedDateRangeSwapEnabled,
  saveAssetSelection,
  stepScheduledReportToNow,
  computeCapsule,
  onReportSyncStoreCommentRemoval,
  onReportSyncStoreCommentInsertion,
  onReportSyncStoreCommentUpdate,
  onReportSyncStore,
  onReportSyncView,
  fetchReport,
  setReportView,
  fetchDateRange,
  fetchAssetSelection,
  fetchMultipleContent,
  fetchComment,
  onReportSyncStoreDateRangeRemoval,
  onReportSyncStoreDateRangeInsertion,
  onReportSyncStoreDateRangeUpdate,
  onReportSyncStoreContentUpdate,
  onReportSyncStoreAssetSelectionInsertion,
  onReportSyncStoreAssetSelectionUpdate,
  clearBackupPreview,
  syncServerAndStoreToBackup,
  onEditingStateEvent,
  updateNextRunTime,
  setSandboxMode,
  updateDateRange,
  setDateRange,
  setBulkSize,
};
export const extraExposedForTesting = {
  loadReport,
  setContentHashCode,
};

export const throttledUpdateNextRunTime = _.throttle(exposedForTesting.updateNextRunTime, 5000);
